import { useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Mail,
  MessageSquare,
  Users,
  Send,
  Clock,
  Sparkles,
  TrendingUp,
  Filter,
  Loader2,
} from "lucide-react";
import { computeCustomerOverviewStats } from "@/lib/customer-overview-stats";
import { useBusiness } from "@/hooks/use-business";
import { useCustomers } from "@/hooks/use-customers";
import { useMarketingCampaigns } from "@/hooks/use-marketing-campaigns";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";
import { addMinutes, format, isBefore, isSameDay, startOfDay } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { cleanPhoneNumber } from "@/lib/phone-utils";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

const generateTimeSlots = (openTime: string, closeTime: string, intervalMinutes: number = 30): string[] => {
  const slots: string[] = [];
  const [openHour, openMinute] = openTime.split(":").map(Number);
  const [closeHour, closeMinute] = closeTime.split(":").map(Number);

  let currentMinutes = openHour * 60 + openMinute;
  const closeMinutes = closeHour * 60 + closeMinute;

  while (currentMinutes < closeMinutes) {
    const hours = Math.floor(currentMinutes / 60);
    const minutes = currentMinutes % 60;
    slots.push(`${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`);
    currentMinutes += intervalMinutes;
  }

  return slots;
};

const MARKETING_DRAFT_VERSION = 1 as const;

type MarketingDraftPersisted = {
  v: typeof MARKETING_DRAFT_VERSION;
  mainTab: "create" | "campaigns";
  selectedSegment: string;
  birthdayAudienceDate: string;
  messageContent: string;
  campaignName: string;
  subject: string;
  channel: "email" | "sms";
  historyFilter: "all" | "email" | "sms";
  scheduleMode: "now" | "later";
  scheduledDateYmd: string | null;
  scheduledTime: string | null;
};

function marketingDraftKey(businessId: string) {
  return `buukd:marketing-draft:v${MARKETING_DRAFT_VERSION}:${businessId}`;
}

function parseYmdToLocalDate(ymd: string | null): Date | null {
  if (!ymd || ymd.length < 10) return null;
  const d = new Date(`${ymd.slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export default function Marketing() {
  const { business, loading: businessLoading } = useBusiness();
  const { customers, loading: customersLoading } = useCustomers();
  const { campaigns, loading: campaignsLoading, createCampaign, updateCampaign } = useMarketingCampaigns();
  const { toast } = useToast();
  const [selectedSegment, setSelectedSegment] = useState("all");
  const [birthdayAudienceDate, setBirthdayAudienceDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [messageContent, setMessageContent] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [subject, setSubject] = useState("");
  const [channel, setChannel] = useState<"email" | "sms">("email");
  const [historyFilter, setHistoryFilter] = useState<"all" | "email" | "sms">("all");
  const [mainTab, setMainTab] = useState<"create" | "campaigns">("create");
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [scheduleMode, setScheduleMode] = useState<"now" | "later">("now");
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [scheduledTime, setScheduledTime] = useState<string | null>(null);
  const scheduleTimeSlots = useMemo(() => generateTimeSlots("08:00", "20:00", 30), []);
  const today = startOfDay(new Date());
  const [draftHydrated, setDraftHydrated] = useState(false);
  const lastPersistedJson = useRef<string | null>(null);

  const isLoading = businessLoading || customersLoading || campaignsLoading;

  // Restore composer + tab state from localStorage (survives tab switch / mobile background).
  useEffect(() => {
    if (!business?.id) {
      setDraftHydrated(false);
      lastPersistedJson.current = null;
      return;
    }
    setDraftHydrated(false);
    lastPersistedJson.current = null;
    try {
      const raw = localStorage.getItem(marketingDraftKey(business.id));
      if (!raw) {
        setDraftHydrated(true);
        return;
      }
      const p = JSON.parse(raw) as Partial<MarketingDraftPersisted>;
      if (p.v !== MARKETING_DRAFT_VERSION) {
        setDraftHydrated(true);
        return;
      }
      if (typeof p.mainTab === "string" && (p.mainTab === "create" || p.mainTab === "campaigns")) {
        setMainTab(p.mainTab);
      }
      if (typeof p.selectedSegment === "string") setSelectedSegment(p.selectedSegment);
      if (typeof p.birthdayAudienceDate === "string") setBirthdayAudienceDate(p.birthdayAudienceDate);
      if (typeof p.messageContent === "string") setMessageContent(p.messageContent);
      if (typeof p.campaignName === "string") setCampaignName(p.campaignName);
      if (typeof p.subject === "string") setSubject(p.subject);
      if (p.channel === "email" || p.channel === "sms") setChannel(p.channel);
      if (p.historyFilter === "all" || p.historyFilter === "email" || p.historyFilter === "sms") {
        setHistoryFilter(p.historyFilter);
      }
      if (p.scheduleMode === "now" || p.scheduleMode === "later") setScheduleMode(p.scheduleMode);
      setScheduledDate(parseYmdToLocalDate(p.scheduledDateYmd ?? null));
      if (typeof p.scheduledTime === "string" || p.scheduledTime === null) {
        setScheduledTime(p.scheduledTime ?? null);
      }
    } catch {
      // ignore corrupt storage
    } finally {
      setDraftHydrated(true);
    }
  }, [business?.id]);

  // Debounced persist while editing (per business).
  useEffect(() => {
    if (!draftHydrated || !business?.id) return;
    const payload: MarketingDraftPersisted = {
      v: MARKETING_DRAFT_VERSION,
      mainTab,
      selectedSegment,
      birthdayAudienceDate,
      messageContent,
      campaignName,
      subject,
      channel,
      historyFilter,
      scheduleMode,
      scheduledDateYmd: scheduledDate ? format(scheduledDate, "yyyy-MM-dd") : null,
      scheduledTime,
    };
    const json = JSON.stringify(payload);
    if (json === lastPersistedJson.current) return;
    const t = window.setTimeout(() => {
      try {
        localStorage.setItem(marketingDraftKey(business.id), json);
        lastPersistedJson.current = json;
      } catch {
        // quota / private mode
      }
    }, 350);
    return () => window.clearTimeout(t);
  }, [
    draftHydrated,
    business?.id,
    mainTab,
    selectedSegment,
    birthdayAudienceDate,
    messageContent,
    campaignName,
    subject,
    channel,
    historyFilter,
    scheduleMode,
    scheduledDate,
    scheduledTime,
  ]);

  const birthdayMonthDay = useMemo(() => {
    // YYYY-MM-DD -> MM-DD
    const s = String(birthdayAudienceDate || "").trim();
    return s.length >= 10 ? s.slice(5, 10) : "";
  }, [birthdayAudienceDate]);

  const isBirthdayMatch = (customerBirthday: unknown) => {
    if (!birthdayMonthDay) return false;
    if (!customerBirthday) return false;
    const s = String(customerBirthday);
    // Supabase `date` columns come back as YYYY-MM-DD
    if (s.length < 10) return false;
    return s.slice(5, 10) === birthdayMonthDay;
  };

  const customerSegments = useMemo(() => {
    const now = Date.now();
    const days = (n: number) => n * 24 * 60 * 60 * 1000;
    const { total, active30, vip, new30 } = computeCustomerOverviewStats(customers);

    const inactive60 = customers.filter((c) => {
      if (!c.lastVisit) return true;
      return now - new Date(c.lastVisit).getTime() >= days(60);
    }).length;
    const hasEmail = customers.filter((c) => !!c.email).length;
    const hasPhone = customers.filter((c) => !!c.phone).length;
    const birthdays = customers.filter((c) => isBirthdayMatch((c as any).birthday)).length;
    const birthdayLabelDate = (() => {
      const d = new Date(`${birthdayAudienceDate}T00:00:00`);
      return isNaN(d.getTime()) ? "" : format(d, "MMM d");
    })();

    return [
      { id: "all", label: "All Customers", count: total },
      { id: "active", label: "Active (last 30 days)", count: active30 },
      { id: "inactive", label: "Inactive (60+ days)", count: inactive60 },
      { id: "vip", label: "VIP (5+ bookings)", count: vip },
      { id: "new", label: "New Customers (last 30 days)", count: new30 },
      { id: "birthday", label: `Birthdays${birthdayLabelDate ? ` (${birthdayLabelDate})` : ""}`, count: birthdays },
      { id: "email", label: "Has Email", count: hasEmail },
      { id: "sms", label: "Has Phone", count: hasPhone },
    ];
  }, [customers, birthdayAudienceDate, birthdayMonthDay]);

  const selectedSegmentCount =
    customerSegments.find((s) => s.id === selectedSegment)?.count ?? 0;

  const getRecipientsCountForChannel = () => {
    const now = Date.now();
    const days = (n: number) => n * 24 * 60 * 60 * 1000;

    const segmentFiltered = customers.filter((c) => {
      if (selectedSegment === "all") return true;
      if (selectedSegment === "active") return !!c.lastVisit && now - new Date(c.lastVisit).getTime() <= days(30);
      if (selectedSegment === "inactive") return !c.lastVisit || now - new Date(c.lastVisit).getTime() >= days(60);
      if (selectedSegment === "vip") return (c.totalAppointments || 0) >= 5;
      if (selectedSegment === "new") return now - new Date(c.created_at).getTime() <= days(30);
      if (selectedSegment === "birthday") return isBirthdayMatch((c as any).birthday);
      if (selectedSegment === "email") return !!c.email;
      if (selectedSegment === "sms") return !!c.phone;
      return true;
    });

    return segmentFiltered.filter((c) => (channel === "email" ? !!c.email : !!c.phone)).length;
  };

  const getRecipientIdsForChannel = () => {
    const now = Date.now();
    const days = (n: number) => n * 24 * 60 * 60 * 1000;

    const segmentFiltered = customers.filter((c) => {
      if (selectedSegment === "all") return true;
      if (selectedSegment === "active") return !!c.lastVisit && now - new Date(c.lastVisit).getTime() <= days(30);
      if (selectedSegment === "inactive") return !c.lastVisit || now - new Date(c.lastVisit).getTime() >= days(60);
      if (selectedSegment === "vip") return (c.totalAppointments || 0) >= 5;
      if (selectedSegment === "new") return now - new Date(c.created_at).getTime() <= days(30);
      if (selectedSegment === "birthday") return isBirthdayMatch((c as any).birthday);
      if (selectedSegment === "email") return !!c.email;
      if (selectedSegment === "sms") return !!c.phone;
      return true;
    });

    return segmentFiltered
      .filter((c) => (channel === "email" ? !!c.email : !!c.phone))
      .map((c) => c.id);
  };

  const getRecipientsForSMS = () => {
    const now = Date.now();
    const days = (n: number) => n * 24 * 60 * 60 * 1000;

    const segmentFiltered = customers.filter((c) => {
      if (selectedSegment === "all") return true;
      if (selectedSegment === "active") return !!c.lastVisit && now - new Date(c.lastVisit).getTime() <= days(30);
      if (selectedSegment === "inactive") return !c.lastVisit || now - new Date(c.lastVisit).getTime() >= days(60);
      if (selectedSegment === "vip") return (c.totalAppointments || 0) >= 5;
      if (selectedSegment === "new") return now - new Date(c.created_at).getTime() <= days(30);
      if (selectedSegment === "birthday") return isBirthdayMatch((c as any).birthday);
      if (selectedSegment === "email") return !!c.email;
      if (selectedSegment === "sms") return !!c.phone;
      return true;
    });

    return segmentFiltered
      .filter((c) => !!c.phone && c.sms_opt_in !== false)
      .map((c) => {
        const cleanedPhone = cleanPhoneNumber(c.phone);
        return cleanedPhone ? { phone: cleanedPhone, customerId: c.id } : null;
      })
      .filter((r): r is { phone: string; customerId: string } => r !== null);
  };

  const getRecipientIdsForEmail = () => {
    const now = Date.now();
    const days = (n: number) => n * 24 * 60 * 60 * 1000;

    const segmentFiltered = customers.filter((c) => {
      if (selectedSegment === "all") return true;
      if (selectedSegment === "active") return !!c.lastVisit && now - new Date(c.lastVisit).getTime() <= days(30);
      if (selectedSegment === "inactive") return !c.lastVisit || now - new Date(c.lastVisit).getTime() >= days(60);
      if (selectedSegment === "vip") return (c.totalAppointments || 0) >= 5;
      if (selectedSegment === "new") return now - new Date(c.created_at).getTime() <= days(30);
      if (selectedSegment === "birthday") return isBirthdayMatch((c as any).birthday);
      if (selectedSegment === "email") return !!c.email;
      if (selectedSegment === "sms") return !!c.phone;
      return true;
    });

    return segmentFiltered
      .filter((c) => !!c.email && (c as any).email_opt_in !== false)
      .map((c) => c.id);
  };

  const handleSaveCampaign = async (mode: "draft" | "sent") => {
    if (!business) {
      toast({ title: "No business found", variant: "destructive" });
      return;
    }
    if (!campaignName.trim()) {
      toast({ title: "Campaign name is required", variant: "destructive" });
      return;
    }
    if (channel === "email" && !subject.trim()) {
      toast({ title: "Subject is required for email", variant: "destructive" });
      return;
    }
    if (!messageContent.trim()) {
      toast({ title: "Message is required", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      // For SMS "Send", we create as draft first, attempt sending, then mark as sent.
      const shouldSendNow = mode === "sent";
      const wantsSchedule = shouldSendNow && scheduleMode === "later";
      let scheduledAtISO: string | null = null;

      if (wantsSchedule) {
        if (!scheduledDate || !scheduledTime) {
          toast({
            title: "Pick a date & time",
            description: "Select when you want this campaign to be sent.",
            variant: "destructive",
          });
          setIsSaving(false);
          return;
        }

        const [h, m] = scheduledTime.split(":").map(Number);
        const dt = new Date(scheduledDate);
        dt.setHours(h, m, 0, 0);

        if (isBefore(dt, new Date())) {
          toast({
            title: "Scheduled time is in the past",
            description: "Pick a future time.",
            variant: "destructive",
          });
          setIsSaving(false);
          return;
        }

        scheduledAtISO = dt.toISOString();
      }

      if (wantsSchedule && !scheduledAtISO) {
        toast({
          title: "Pick a date & time",
          description: "Select when you want this campaign to be sent.",
          variant: "destructive",
        });
        setIsSaving(false);
        return;
      }

      const initialStatus =
        wantsSchedule ? "scheduled" : channel === "sms" && shouldSendNow ? "draft" : mode;

      const recipientIdsForStorage =
        channel === "email"
          ? getRecipientIdsForEmail()
          : getRecipientsForSMS().map((r) => r.customerId);

      const created = await createCampaign({
        business_id: business.id,
        name: campaignName.trim(),
        type: channel,
        status: initialStatus,
        segment_id: selectedSegment,
        subject: channel === "email" ? subject.trim() : null,
        message: messageContent.trim(),
        recipients_count: getRecipientsCountForChannel(),
        scheduled_at: wantsSchedule ? scheduledAtISO : null,
        sent_at: null,
        open_rate: null,
        click_rate: null,
        recipient_ids: recipientIdsForStorage,
      } as any);

      // If scheduled, don't send immediately
      if (wantsSchedule) {
        toast({
          title: "Campaign scheduled",
          description: `This campaign will be sent on ${format(new Date(scheduledAtISO!), "MMM d, yyyy h:mm a")}.`,
        });
      } else
      if (channel === "sms" && shouldSendNow) {
        const recipients = getRecipientsForSMS();
        if (recipients.length === 0) {
          toast({
            title: "No SMS recipients",
            description: "No customers with phone numbers and SMS opt-in found in this segment.",
            variant: "destructive",
          });
          setIsSaving(false);
          return;
        }

        // Get current session to ensure we have a valid token
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData?.session) {
          toast({
            title: "Authentication error",
            description: "Please log out and log back in to continue.",
            variant: "destructive",
          });
          setIsSaving(false);
          return;
        }

        logger.info("Sending SMS campaign", { recipientCount: recipients.length });
        logger.debug("Session access token check", { hasToken: !!sessionData.session.access_token });
        
        // IMPORTANT: Do not override headers here.
        // supabase-js automatically sends `apikey` + the current session `Authorization` header.
        // Overriding headers can drop `apikey`, causing a 401 *before* the function runs.
        const { data: sendResult, error: sendErr } = await supabase.functions.invoke(
          "send-sms-campaign",
          {
            body: {
              campaignId: created.id,
              businessId: business.id,
              recipients,
              message: messageContent.trim(),
              senderId: "BuukdSMS",
              optOutKeyword: "STOP",
            },
          }
        );
        
        if (sendErr) {
          logger.error("SMS send error", sendErr instanceof Error ? sendErr : new Error(String(sendErr)), {
            errorDetails: JSON.stringify(sendErr, null, 2),
          });
          throw new Error(sendErr.message || "Failed to send SMS");
        }

        await updateCampaign(created.id, {
          status: "sent",
          sent_at: new Date().toISOString(),
        } as any);

        toast({
          title: "SMS campaign sent",
          description: `Sent ${sendResult?.sent ?? 0}/${sendResult?.attempted ?? recipients.length}.`,
        });
      } else if (channel === "email" && shouldSendNow) {
        const recipientIds = getRecipientIdsForEmail();
        if (recipientIds.length === 0) {
          toast({
            title: "No email recipients",
            description: "No customers with email and opt-in found in this segment.",
            variant: "destructive",
          });
          setIsSaving(false);
          return;
        }

        const { data: sendResult, error: sendErr } = await supabase.functions.invoke("send-email", {
          body: {
            type: "marketing_campaign",
            campaignId: created.id,
            businessId: business.id,
            recipientIds,
          },
        });
        if (sendErr) throw sendErr;

        await updateCampaign(created.id, {
          status: "sent",
          sent_at: new Date().toISOString(),
        } as any);

        toast({
          title: "Email campaign sent",
          description: `Sent ${sendResult?.sent ?? 0}/${sendResult?.attempted ?? recipientIds.length}.`,
        });
      } else if (mode === "sent") {
        await updateCampaign(created.id, {
    status: "sent",
          sent_at: new Date().toISOString(),
        } as any);
      }

      toast({
        title: mode === "sent" ? "Campaign saved as sent" : "Draft saved",
        description: "Campaign history has been updated.",
      });

      setCampaignName("");
      setSubject("");
      setMessageContent("");
      setSelectedSegment("all");
      setChannel("email");
      setScheduleMode("now");
      setScheduledDate(null);
      setScheduledTime(null);
      setMainTab("create");
      try {
        if (business?.id) {
          localStorage.removeItem(marketingDraftKey(business.id));
          lastPersistedJson.current = null;
        }
      } catch {
        // ignore
      }
    } catch (e: any) {
      toast({ title: "Failed to save campaign", description: e?.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const selectedCampaign = useMemo(() => {
    if (!selectedCampaignId) return null;
    return campaigns.find((c) => c.id === selectedCampaignId) || null;
  }, [campaigns, selectedCampaignId]);

  const openCampaignDetails = (id: string) => {
    setSelectedCampaignId(id);
    setIsDetailsOpen(true);
  };

  const sendDraftNow = async () => {
    if (!business || !selectedCampaign) return;
    setIsSaving(true);
    try {
      // If SMS, actually send it via Edge Function
      if (selectedCampaign.type === "sms") {
        // Get current recipients for the same segment (or reuse stored recipients if available)
        const now = Date.now();
        const days = (n: number) => n * 24 * 60 * 60 * 1000;

        const storedRecipientIds = ((selectedCampaign as any)?.recipient_ids as string[] | null) || null;
        const segmentFiltered = storedRecipientIds?.length
          ? customers.filter((c) => storedRecipientIds.includes(c.id))
          : customers.filter((c) => {
              const seg = selectedCampaign.segment_id;
              if (seg === "all") return true;
              if (seg === "active") return !!c.lastVisit && now - new Date(c.lastVisit).getTime() <= days(30);
              if (seg === "inactive") return !c.lastVisit || now - new Date(c.lastVisit).getTime() >= days(60);
              if (seg === "vip") return (c.totalAppointments || 0) >= 5;
              if (seg === "new") return now - new Date(c.created_at).getTime() <= days(30);
              if (seg === "birthday") return isBirthdayMatch((c as any).birthday);
              if (seg === "email") return !!c.email;
              if (seg === "sms") return !!c.phone;
              return true;
            });

        const recipients = segmentFiltered
          .filter((c) => !!c.phone && c.sms_opt_in !== false)
          .map((c) => {
            const cleanedPhone = cleanPhoneNumber(c.phone);
            return cleanedPhone ? { phone: cleanedPhone, customerId: c.id } : null;
          })
          .filter((r): r is { phone: string; customerId: string } => r !== null);

        if (recipients.length === 0) {
          toast({
            title: "No SMS recipients",
            description: "No customers with phone numbers and SMS opt-in found.",
            variant: "destructive",
          });
          setIsSaving(false);
          setIsDetailsOpen(false);
          return;
        }

        // Get session for auth header
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData?.session) {
          toast({
            title: "Authentication error",
            description: "Please log out and log back in.",
            variant: "destructive",
          });
          setIsSaving(false);
          setIsDetailsOpen(false);
          return;
        }

        const { data: sendResult, error: sendErr } = await supabase.functions.invoke(
          "send-sms-campaign",
          {
            body: {
              campaignId: selectedCampaign.id,
              businessId: business.id,
              recipients,
              message: selectedCampaign.message || "",
              senderId: "BuukdSMS",
              optOutKeyword: "STOP",
            },
          }
        );
        if (sendErr) throw sendErr;

        await updateCampaign(selectedCampaign.id, {
          status: "sent",
          sent_at: new Date().toISOString(),
        } as any);

        toast({
          title: "SMS campaign sent",
          description: `Sent ${sendResult?.sent ?? 0}/${sendResult?.attempted ?? recipients.length}.`,
        });
      } else if (selectedCampaign.type === "email") {
        // Email campaigns: send via Resend-backed Edge Function
        const storedRecipientIds = ((selectedCampaign as any)?.recipient_ids as string[] | null) || null;
        let recipientIds: string[] = [];
        if (storedRecipientIds?.length) {
          recipientIds = storedRecipientIds;
        } else {
          const now = Date.now();
          const days = (n: number) => n * 24 * 60 * 60 * 1000;

          const segmentFiltered = customers.filter((c) => {
            const seg = selectedCampaign.segment_id;
            if (seg === "all") return true;
            if (seg === "active") return !!c.lastVisit && now - new Date(c.lastVisit).getTime() <= days(30);
            if (seg === "inactive") return !c.lastVisit || now - new Date(c.lastVisit).getTime() >= days(60);
            if (seg === "vip") return (c.totalAppointments || 0) >= 5;
            if (seg === "new") return now - new Date(c.created_at).getTime() <= days(30);
            if (seg === "birthday") return isBirthdayMatch((c as any).birthday);
            if (seg === "email") return !!c.email;
            if (seg === "sms") return !!c.phone;
            return true;
          });

          recipientIds = segmentFiltered
            .filter((c) => !!c.email && (c as any).email_opt_in !== false)
            .map((c) => c.id);
        }

        if (recipientIds.length === 0) {
          toast({
            title: "No email recipients",
            description: "No customers with email and opt-in found.",
            variant: "destructive",
          });
          setIsSaving(false);
          setIsDetailsOpen(false);
          return;
        }

        const { data: sendResult, error: sendErr } = await supabase.functions.invoke("send-email", {
          body: {
            type: "marketing_campaign",
            campaignId: selectedCampaign.id,
            businessId: business.id,
            recipientIds,
          },
        });
        if (sendErr) throw sendErr;

        await updateCampaign(selectedCampaign.id, {
          status: "sent",
          sent_at: new Date().toISOString(),
        } as any);

        toast({
          title: "Email campaign sent",
          description: `Sent ${sendResult?.sent ?? 0}/${sendResult?.attempted ?? recipientIds.length}.`,
        });
      } else {
        await updateCampaign(selectedCampaign.id, {
          status: "sent",
          sent_at: new Date().toISOString(),
        } as any);
        toast({ title: "Campaign sent", description: "Marked as sent and saved to history." });
      }

      setIsDetailsOpen(false);
    } catch (e: any) {
      toast({ title: "Failed to send", description: e?.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const resendCampaign = async () => {
    if (!business || !selectedCampaign) return;
    setIsSaving(true);
    try {
      const storedRecipientIds = ((selectedCampaign as any)?.recipient_ids as string[] | null) || null;
      const newCampaign = await createCampaign({
        business_id: business.id,
        name: selectedCampaign.name,
        type: selectedCampaign.type as any,
        status: selectedCampaign.type === "sms" ? "draft" : "draft",
        segment_id: selectedCampaign.segment_id,
        subject: selectedCampaign.subject,
        message: selectedCampaign.message,
        recipients_count: selectedCampaign.recipients_count,
        scheduled_at: null,
        sent_at: null,
        open_rate: null,
        click_rate: null,
        recipient_ids: storedRecipientIds,
      } as any);

      // If SMS, actually send it via Edge Function
      if (selectedCampaign.type === "sms") {
        // Get current recipients for the same segment (or reuse stored recipients if available)
        const now = Date.now();
        const days = (n: number) => n * 24 * 60 * 60 * 1000;

        const segmentFiltered = storedRecipientIds?.length
          ? customers.filter((c) => storedRecipientIds.includes(c.id))
          : customers.filter((c) => {
              const seg = selectedCampaign.segment_id;
              if (seg === "all") return true;
              if (seg === "active") return !!c.lastVisit && now - new Date(c.lastVisit).getTime() <= days(30);
              if (seg === "inactive") return !c.lastVisit || now - new Date(c.lastVisit).getTime() >= days(60);
              if (seg === "vip") return (c.totalAppointments || 0) >= 5;
              if (seg === "new") return now - new Date(c.created_at).getTime() <= days(30);
              if (seg === "birthday") return isBirthdayMatch((c as any).birthday);
              if (seg === "email") return !!c.email;
              if (seg === "sms") return !!c.phone;
              return true;
            });

        const recipients = segmentFiltered
          .filter((c) => !!c.phone && c.sms_opt_in !== false)
          .map((c) => {
            const cleanedPhone = cleanPhoneNumber(c.phone);
            return cleanedPhone ? { phone: cleanedPhone, customerId: c.id } : null;
          })
          .filter((r): r is { phone: string; customerId: string } => r !== null);

        if (recipients.length === 0) {
          toast({
            title: "No SMS recipients",
            description: "No customers with phone numbers and SMS opt-in found.",
            variant: "destructive",
          });
          setIsSaving(false);
          setIsDetailsOpen(false);
          return;
        }

        // Get session for auth header
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData?.session) {
          toast({
            title: "Authentication error",
            description: "Please log out and log back in.",
            variant: "destructive",
          });
          setIsSaving(false);
          setIsDetailsOpen(false);
          return;
        }

        const { data: sendResult, error: sendErr } = await supabase.functions.invoke(
          "send-sms-campaign",
          {
            body: {
              campaignId: newCampaign.id,
              businessId: business.id,
              recipients,
              message: selectedCampaign.message || "",
              senderId: "BuukdSMS",
              optOutKeyword: "STOP",
            },
          }
        );
        if (sendErr) throw sendErr;

        await updateCampaign(newCampaign.id, {
          status: "sent",
          sent_at: new Date().toISOString(),
        } as any);

        toast({
          title: "SMS campaign resent",
          description: `Sent ${sendResult?.sent ?? 0}/${sendResult?.attempted ?? recipients.length}.`,
        });
      } else if (selectedCampaign.type === "email") {
        let recipientIds: string[] = [];
        if (storedRecipientIds?.length) {
          recipientIds = storedRecipientIds;
        } else {
          const now = Date.now();
          const days = (n: number) => n * 24 * 60 * 60 * 1000;

          const segmentFiltered = customers.filter((c) => {
            const seg = selectedCampaign.segment_id;
            if (seg === "all") return true;
            if (seg === "active") return !!c.lastVisit && now - new Date(c.lastVisit).getTime() <= days(30);
            if (seg === "inactive") return !c.lastVisit || now - new Date(c.lastVisit).getTime() >= days(60);
            if (seg === "vip") return (c.totalAppointments || 0) >= 5;
            if (seg === "new") return now - new Date(c.created_at).getTime() <= days(30);
            if (seg === "birthday") return isBirthdayMatch((c as any).birthday);
            if (seg === "email") return !!c.email;
            if (seg === "sms") return !!c.phone;
            return true;
          });

          recipientIds = segmentFiltered
            .filter((c) => !!c.email && (c as any).email_opt_in !== false)
            .map((c) => c.id);
        }

        if (recipientIds.length === 0) {
          toast({
            title: "No email recipients",
            description: "No customers with email and opt-in found.",
            variant: "destructive",
          });
          setIsSaving(false);
          setIsDetailsOpen(false);
          return;
        }

        const { data: sendResult, error: sendErr } = await supabase.functions.invoke("send-email", {
          body: {
            type: "marketing_campaign",
            campaignId: newCampaign.id,
            businessId: business.id,
            recipientIds,
          },
        });
        if (sendErr) throw sendErr;

        await updateCampaign(newCampaign.id, {
          status: "sent",
          sent_at: new Date().toISOString(),
        } as any);

        toast({
          title: "Email campaign resent",
          description: `Sent ${sendResult?.sent ?? 0}/${sendResult?.attempted ?? recipientIds.length}.`,
        });
      } else {
        toast({ title: "Campaign resent", description: "A new sent entry was added to history." });
      }

      setIsDetailsOpen(false);
    } catch (e: any) {
      toast({ title: "Failed to resend", description: e?.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "sent":
        return <Badge className="bg-success/10 text-success border-success/20 hover:bg-success/10">Sent</Badge>;
      case "scheduled":
        return <Badge className="bg-primary/10 text-primary hover:bg-primary/10">Scheduled</Badge>;
      case "draft":
        return <Badge variant="secondary">Draft</Badge>;
      default:
        return null;
    }
  };

  const AudienceSelector = () => (
    <div className="space-y-4">
      {customerSegments.map((segment) => (
        <label
          key={segment.id}
          className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors"
        >
          <Checkbox
            checked={selectedSegment === segment.id}
            onCheckedChange={() => setSelectedSegment(segment.id)}
          />
          <div className="flex-1">
            <p className="font-medium text-sm">{segment.label}</p>
          </div>
          <Badge variant="secondary">{segment.count}</Badge>
        </label>
      ))}

      {selectedSegment === "birthday" && (
        <div className="rounded-lg border border-border bg-card p-3 space-y-2">
          <Label className="text-sm">Birthday date</Label>
          <Input
            type="date"
            value={birthdayAudienceDate}
            onChange={(e) => setBirthdayAudienceDate(e.target.value)}
            className="w-full"
          />
          <p className="text-xs text-muted-foreground">
            We match by month & day (the year doesn’t matter).
          </p>
        </div>
      )}
    </div>
  );

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <div className="space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Marketing</h1>
          <p className="text-sm text-muted-foreground">Send promotional messages to your customers</p>
        </div>

        <Tabs value={mainTab} onValueChange={(v) => setMainTab(v as "create" | "campaigns")} className="w-full">
          {/* Scrollable tabs on mobile */}
          <div className="overflow-x-auto -mx-1 px-1">
            <TabsList className="mb-4 sm:mb-6 bg-muted/50 p-1 rounded-lg inline-flex w-auto min-w-full sm:min-w-0">
              <TabsTrigger value="create" className="rounded-md flex-1 sm:flex-none whitespace-nowrap">Create Campaign</TabsTrigger>
              <TabsTrigger value="campaigns" className="rounded-md flex-1 sm:flex-none whitespace-nowrap">Past Campaigns</TabsTrigger>
            </TabsList>
          </div>

          {/* Create Campaign Tab */}
          <TabsContent value="create" className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Campaign Builder */}
              <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                <Card>
                  <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
                    <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                      <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                      Create New Campaign
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm">Send promotional messages to your customers</CardDescription>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6 pt-2 sm:pt-4 space-y-4 sm:space-y-6">
                    <div className="space-y-2">
                      <Label className="text-sm">Campaign Name</Label>
                      <Input
                        placeholder="e.g., Weekend Special Offer"
                        className="input-sleek"
                        value={campaignName}
                        onChange={(e) => setCampaignName(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Channel</Label>
                      {/* Stacked on mobile, side by side on tablet+ */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <label className="flex items-center gap-3 p-3 sm:p-4 border border-border rounded-xl cursor-pointer hover:border-primary transition-colors [&:has(input:checked)]:border-primary [&:has(input:checked)]:bg-primary/5">
                          <input
                            type="radio"
                            name="channel"
                            value="email"
                            className="sr-only"
                            checked={channel === "email"}
                            onChange={() => setChannel("email")}
                          />
                          <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm sm:text-base">Email</p>
                            <p className="text-xs sm:text-sm text-muted-foreground truncate">Rich content with images</p>
                          </div>
                        </label>
                        <label className="flex items-center gap-3 p-3 sm:p-4 border border-border rounded-xl cursor-pointer hover:border-primary transition-colors [&:has(input:checked)]:border-primary [&:has(input:checked)]:bg-primary/5">
                          <input
                            type="radio"
                            name="channel"
                            value="sms"
                            className="sr-only"
                            checked={channel === "sms"}
                            onChange={() => setChannel("sms")}
                          />
                          <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
                            <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 text-success" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm sm:text-base">SMS</p>
                            <p className="text-xs sm:text-sm text-muted-foreground truncate">Quick text messages</p>
                          </div>
                        </label>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Subject Line</Label>
                      <Input
                        placeholder="e.g., Don't miss our exclusive offer!"
                        className="input-sleek"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        disabled={channel !== "email"}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Message</Label>
                      <Textarea
                        placeholder="Write your promotional message here..."
                        className="input-sleek min-h-[120px] sm:min-h-[150px]"
                        value={messageContent}
                        onChange={(e) => setMessageContent(e.target.value)}
                      />
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between text-xs sm:text-sm gap-1">
                        <p className="text-muted-foreground">
                          Use {"{name}"} to personalize
                        </p>
                        <p className="text-muted-foreground">{messageContent.length}/500</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Schedule</Label>
                      <div className="grid grid-cols-2 gap-3 sm:gap-4">
                        <label className="flex items-center justify-center gap-2 p-3 sm:p-4 border border-border rounded-xl cursor-pointer hover:border-primary transition-colors [&:has(input:checked)]:border-primary [&:has(input:checked)]:bg-primary/5">
                          <input
                            type="radio"
                            name="schedule"
                            value="now"
                            className="sr-only"
                            checked={scheduleMode === "now"}
                            onChange={() => setScheduleMode("now")}
                          />
                          <Send className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                          <span className="font-medium text-sm sm:text-base">Send Now</span>
                        </label>
                        <label className="flex items-center justify-center gap-2 p-3 sm:p-4 border border-border rounded-xl cursor-pointer hover:border-primary transition-colors [&:has(input:checked)]:border-primary [&:has(input:checked)]:bg-primary/5">
                          <input
                            type="radio"
                            name="schedule"
                            value="later"
                            className="sr-only"
                            checked={scheduleMode === "later"}
                            onChange={() => setScheduleMode("later")}
                          />
                          <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                          <span className="font-medium text-sm sm:text-base">Schedule</span>
                        </label>
                      </div>
                    </div>

                    {scheduleMode === "later" && (
                      <div className="space-y-2">
                        <Label className="text-sm">Send date & time</Label>
                        <div className="bg-card rounded-xl border border-border overflow-hidden p-4">
                          <Calendar
                            mode="single"
                            selected={scheduledDate || undefined}
                            onSelect={(date) => {
                              setScheduledDate(date || null);
                              setScheduledTime(null);
                            }}
                            disabled={(date) => isBefore(date, today)}
                            className="w-full max-w-full [&_.rdp]:w-full [&_.rdp-months]:w-full [&_.rdp-month]:w-full [&_.rdp-table]:w-full [&_table]:w-full [&_thead]:w-full [&_tbody]:w-full [&_tr]:grid [&_tr]:grid-cols-7 [&_tr]:w-full [&_td]:flex [&_td]:justify-center [&_th]:flex [&_th]:justify-center [&_.rdp-cell]:flex-1 [&_.rdp-day]:w-full [&_.rdp-day]:h-12 [&_.rdp-day]:text-base"
                          />
                        </div>

                        {scheduledDate && (
                          <div className="bg-card rounded-xl border border-border p-4">
                            <h4 className="font-medium text-foreground mb-3">
                              Choose time for {format(scheduledDate, "EEEE, MMM d")}
                            </h4>
                            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                              {scheduleTimeSlots.map((time) => {
                                const [h, m] = time.split(":").map(Number);
                                const dt = new Date(scheduledDate);
                                dt.setHours(h, m, 0, 0);
                                const disabled = isSameDay(scheduledDate, new Date()) && isBefore(dt, addMinutes(new Date(), 1));
                                return (
                                  <button
                                    key={time}
                                    type="button"
                                    onClick={() => !disabled && setScheduledTime(time)}
                                    disabled={disabled}
                                    className={cn(
                                      "py-2 px-3 rounded-lg text-sm font-medium transition-colors",
                                      disabled
                                        ? "bg-muted text-muted-foreground cursor-not-allowed"
                                        : scheduledTime === time
                                          ? "bg-primary text-primary-foreground"
                                          : "bg-accent hover:bg-accent/80 text-foreground"
                                    )}
                                  >
                                    {time}
                                  </button>
                                );
                              })}
                            </div>
                            <p className="text-xs text-muted-foreground mt-3">
                              Times are in your local timezone. We’ll send automatically at the selected time.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Mobile: Show audience selector button */}
                    <div className="lg:hidden">
                      <Sheet>
                        <SheetTrigger asChild>
                          <Button variant="outline" className="w-full">
                            <Users className="h-4 w-4 mr-2" />
                            Select Audience ({selectedSegmentCount} recipients)
                          </Button>
                        </SheetTrigger>
                        <SheetContent side="bottom" className="h-[70vh]">
                          <SheetHeader>
                            <SheetTitle>Select Audience</SheetTitle>
                            <SheetDescription>Choose who receives this campaign</SheetDescription>
                          </SheetHeader>
                          <div className="mt-6">
                            <AudienceSelector />
                          </div>
                        </SheetContent>
                      </Sheet>
                    </div>

                    {/* Stacked buttons on mobile */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-2 sm:pt-4">
                      <Button
                        variant="outline"
                        className="flex-1 order-2 sm:order-1"
                        onClick={() => handleSaveCampaign("draft")}
                        disabled={isSaving}
                      >
                        Save as Draft
                      </Button>
                      <Button
                        className="flex-1 order-1 sm:order-2"
                        onClick={() => handleSaveCampaign("sent")}
                        disabled={isSaving}
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Send Campaign
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Audience Selector - Hidden on mobile (shown in sheet) */}
              <div className="hidden lg:block space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="h-5 w-5 text-primary" />
                      Select Audience
                    </CardTitle>
                    <CardDescription>Choose who receives this campaign</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <AudienceSelector />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-primary" />
                      Tips
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm text-muted-foreground">
                    <p>• Keep subject lines under 50 characters</p>
                    <p>• Include a clear call-to-action</p>
                    <p>• Best times to send: Tue-Thu, 10am-2pm</p>
                    <p>• Personalize with customer names</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Past Campaigns Tab */}
          <TabsContent value="campaigns" className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <CardTitle className="text-base sm:text-lg">Campaign History</CardTitle>
                  <Select value={historyFilter} onValueChange={(v) => setHistoryFilter(v as any)}>
                    <SelectTrigger className="w-full sm:w-40">
                      <Filter className="h-4 w-4 mr-2 sm:hidden" />
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Campaigns</SelectItem>
                      <SelectItem value="email">Email Only</SelectItem>
                      <SelectItem value="sms">SMS Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                {campaignsLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (historyFilter === "all"
                    ? campaigns
                    : campaigns.filter((c) => c.type === historyFilter)
                  ).length === 0 ? (
                  <div className="py-10 text-center text-sm text-muted-foreground">
                    No campaigns yet. Create one from the “Create Campaign” tab.
                  </div>
                ) : (
                <div className="space-y-3 sm:space-y-4">
                    {(historyFilter === "all"
                      ? campaigns
                      : campaigns.filter((c) => c.type === historyFilter)
                    ).map((c) => {
                      const date = (c.sent_at || c.scheduled_at || c.created_at) as string;
                      const dateLabel = date ? format(new Date(date), "MMM d, yyyy") : null;
                      return (
                        <div
                          key={c.id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border border-border rounded-xl hover:bg-accent/50 transition-colors gap-3 cursor-pointer"
                          role="button"
                          tabIndex={0}
                          onClick={() => openCampaignDetails(c.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") openCampaignDetails(c.id);
                          }}
                    >
                      <div className="flex items-center gap-3 sm:gap-4">
                            <div
                              className={`h-8 w-8 sm:h-10 sm:w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                c.type === "email" ? "bg-primary/10" : "bg-success/10"
                              }`}
                            >
                              {c.type === "email" ? (
                                <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                          ) : (
                            <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 text-success" />
                          )}
                        </div>
                        <div className="min-w-0">
                              <p className="font-medium text-sm sm:text-base truncate">{c.name}</p>
                          <p className="text-xs sm:text-sm text-muted-foreground">
                                {c.recipients_count} recipients
                                {dateLabel ? ` • ${dateLabel}` : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6 pl-11 sm:pl-0">
                            {c.open_rate != null && (
                          <div className="text-center">
                                <p className="text-xs sm:text-sm font-medium">{c.open_rate}%</p>
                            <p className="text-xs text-muted-foreground">Opens</p>
                          </div>
                        )}
                            {c.click_rate != null && (
                          <div className="text-center">
                                <p className="text-xs sm:text-sm font-medium">{c.click_rate}%</p>
                            <p className="text-xs text-muted-foreground">Clicks</p>
                          </div>
                        )}
                            {getStatusBadge(c.status)}
                      </div>
                    </div>
                      );
                    })}
                </div>
                )}
              </CardContent>
            </Card>

            <Sheet open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
              <SheetContent side="right" className="w-full sm:max-w-lg">
                <SheetHeader>
                  <SheetTitle>Campaign</SheetTitle>
                  <SheetDescription>
                    View content and actions (send/resend).
                  </SheetDescription>
                </SheetHeader>

                {selectedCampaign ? (
                  <div className="mt-6 space-y-4">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Name</p>
                      <p className="font-medium">{selectedCampaign.name}</p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="capitalize">{selectedCampaign.type}</Badge>
                      {getStatusBadge(selectedCampaign.status)}
                      <Badge variant="secondary">
                        {selectedCampaign.recipients_count} recipients
                      </Badge>
                    </div>

                    {selectedCampaign.type === "email" && (
                      <div className="space-y-2">
                        <Label className="text-sm">Subject</Label>
                        <Input value={selectedCampaign.subject || ""} readOnly className="input-sleek" />
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label className="text-sm">Message</Label>
                      <Textarea
                        value={selectedCampaign.message || ""}
                        readOnly
                        className="input-sleek min-h-[180px]"
                      />
                    </div>

                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div className="flex justify-between">
                        <span>Created</span>
                        <span>{format(new Date(selectedCampaign.created_at), "MMM d, yyyy p")}</span>
                      </div>
                      {selectedCampaign.sent_at && (
                        <div className="flex justify-between">
                          <span>Sent</span>
                          <span>{format(new Date(selectedCampaign.sent_at), "MMM d, yyyy p")}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                      {selectedCampaign.status === "draft" ? (
                        <Button className="flex-1" onClick={sendDraftNow} disabled={isSaving}>
                          <Send className="h-4 w-4 mr-2" />
                          Send now
                        </Button>
                      ) : (
                        <Button className="flex-1" onClick={resendCampaign} disabled={isSaving}>
                          <Send className="h-4 w-4 mr-2" />
                          Resend
                        </Button>
                      )}
                      <Button variant="outline" className="flex-1" onClick={() => setIsDetailsOpen(false)}>
                        Close
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-8 text-sm text-muted-foreground">
                    Select a campaign to view details.
                  </div>
                )}
              </SheetContent>
            </Sheet>
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
